import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PageHeader } from "@/components/app/page-header";
import { HelpPanel } from "@/components/app/help-panel";
import { listUserCampaignsForDebug } from "@/lib/campaign-pixel-hits.functions";
import { getOrCreateCampaignAlias } from "@/lib/campaign-alias.functions";
import { importRecipientsCsv } from "@/lib/recipient-import.functions";
import { minimizePixelSnippet } from "@/lib/pixel-snippet";
import { Copy, Upload, CheckCircle2, Info } from "lucide-react";

export const Route = createFileRoute("/_authenticated/recipient-import")({
  component: RecipientImportPage,
});

const ORIGIN = "https://meineasykredit.click";
const ASSET_HOST_SUGGESTIONS = [
  "meineasykredit.click",
  "img.meineasykredit.click",
  "assets.meineasykredit.click",
  "media.meineasykredit.click",
];

function normalizeAssetHost(input: string): string {
  const v = (input || "").trim().toLowerCase();
  if (!v) return ORIGIN;
  if (/^https?:\/\//.test(v)) return v.replace(/\/+$/, "");
  return `https://${v.replace(/\/+$/, "")}`;
}

type ParsedRow = { recipient_email: string; sender_email?: string | null };

function parseCsv(text: string): { rows: ParsedRow[]; errors: string[] } {
  const errors: string[] = [];
  const rows: ParsedRow[] = [];
  const lines = text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  if (lines.length === 0) return { rows, errors: ["CSV ist leer."] };

  // Header erkennen
  const firstCells = lines[0]!.split(/[,;\t]/).map((c) => c.trim().toLowerCase());
  const hasHeader = firstCells.some((c) =>
    ["email", "recipient_email", "recipient", "lead_email"].includes(c),
  );
  let emailIdx = 0;
  let senderIdx = -1;
  let start = 0;
  if (hasHeader) {
    emailIdx = firstCells.findIndex((c) =>
      ["email", "recipient_email", "recipient", "lead_email"].includes(c),
    );
    senderIdx = firstCells.findIndex((c) =>
      ["sender", "sender_email", "from", "from_email"].includes(c),
    );
    start = 1;
  }
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  for (let i = start; i < lines.length; i++) {
    const cells = lines[i]!.split(/[,;\t]/).map((c) => c.trim().replace(/^["']|["']$/g, ""));
    const email = cells[emailIdx];
    if (!email || !EMAIL_RE.test(email)) {
      errors.push(`Zeile ${i + 1}: ungueltige E-Mail "${email ?? ""}"`);
      continue;
    }
    const sender = senderIdx >= 0 ? cells[senderIdx] : undefined;
    rows.push({
      recipient_email: email.toLowerCase(),
      sender_email: sender && EMAIL_RE.test(sender) ? sender.toLowerCase() : null,
    });
  }
  return { rows, errors };
}

function RecipientImportPage() {
  const listCampaigns = useServerFn(listUserCampaignsForDebug);
  const ensureAlias = useServerFn(getOrCreateCampaignAlias);
  const importCsv = useServerFn(importRecipientsCsv);

  const { data: campaigns = [] } = useQuery({
    queryKey: ["recipient-import", "campaigns"],
    queryFn: () => listCampaigns(),
  });

  const [campaignId, setCampaignId] = useState<string>("");
  const [csvText, setCsvText] = useState<string>("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{
    ok: boolean;
    total: number;
    created: number;
    updated: number;
    errors: number;
  } | null>(null);
  const [shortToken, setShortToken] = useState<string>("");
  const [assetHost, setAssetHost] = useState<string>("meineasykredit.click");

  const parsed = useMemo(() => parseCsv(csvText), [csvText]);

  async function loadShortToken(id: string) {
    if (!id) return setShortToken("");
    try {
      const r = await ensureAlias({ data: { campaignId: id } });
      setShortToken(r.shortToken ?? "");
    } catch (e) {
      setShortToken("");
      toast.error((e as Error).message);
    }
  }

  async function handleImport() {
    if (!campaignId) return toast.error("Bitte Kampagne waehlen.");
    if (parsed.rows.length === 0) return toast.error("Keine gueltigen Zeilen.");
    setBusy(true);
    setResult(null);
    try {
      const r = await importCsv({
        data: { campaign_id: campaignId, rows: parsed.rows },
      });
      setResult(r);
      toast.success(`Import: ${r.created} neu, ${r.updated} aktualisiert, ${r.errors} Fehler`);
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setCsvText(String(reader.result ?? ""));
    reader.readAsText(f);
  }

  const assetOrigin = useMemo(() => normalizeAssetHost(assetHost), [assetHost]);
  // Raw-URL (mit allen Merge-Tags) wird durch minimizePixelSnippet auf das
  // absolute Minimum reduziert: nur die vom Backend gelesenen Parameter
  // (`s`, `r`) bleiben übrig, alles andere fliegt automatisch raus.
  const rawPixelUrl = shortToken
    ? `${assetOrigin}/a/${shortToken}.png?s={{sender_email}}&r={{email}}`
    : "";
  const { url: pixelUrl, snippet: minimalSnippet } = useMemo(
    () => minimizePixelSnippet(rawPixelUrl),
    [rawPixelUrl],
  );

  function copy(text: string) {
    if (!text) return;
    navigator.clipboard.writeText(text);
    toast.success("In Zwischenablage kopiert");
  }

  return (
    <div className="p-6 space-y-6">
      <PageHeader
        title="Empfaenger-Import (Plusvibe-Workflow)"
        description="Lade dieselbe CSV hoch, die du in Plusvibe verwendest. Lovable speichert Kampagne, Sender und Empfaenger fuer spaeteres Message-ID / Bounce-Matching. Plusvibe versendet wie gewohnt."
      />

      <HelpPanel
        title="Hilfe: Empfänger-Liste hochladen"
        what={
          <>
            Hier lädst du eine <b>Liste mit E-Mail-Adressen</b> hoch (CSV-Datei aus Excel oder
            Google Sheets). Diese Liste kannst du danach in deinen Kampagnen anwählen.
          </>
        }
        why={
          <>
            Ohne Empfänger keine Kampagne. Außerdem merkt sich das System pro Empfänger, ob er
            geöffnet hat oder ge-bounct ist — so kannst du tote Adressen aussortieren.
          </>
        }
        how={[
          <>
            <b>Kampagne auswählen</b> oben (oder vorher eine neue anlegen unter "Kampagnen").
          </>,
          <>
            <b>CSV-Datei hochladen</b>: erste Spalte = E-Mail, optional zweite Spalte = Name.
            Beispiel: <code>max@firma.de,Max Müller</code>
          </>,
          <>Oder direkt im Textfeld eine E-Mail pro Zeile reinkopieren.</>,
          <>
            Auf <b>Import</b> klicken — Doppelte Adressen werden automatisch übersprungen.
          </>,
          <>
            Fertig. Die Liste erscheint in der Tabelle unten und ist in jedem Kampagnen-Wizard
            verfügbar.
          </>,
        ]}
        tips={[
          <>Excel: "Datei → Speichern unter → CSV UTF-8" wählen, sonst werden Umlaute komisch.</>,
          <>
            Adressen mit Tippfehlern (z.B. fehlt das @) werden beim Import angezeigt — bitte
            korrigieren.
          </>,
          <>
            Gekaufte Listen sind eine schlechte Idee — meist Spam-Fallen, die deine Reputation
            ruinieren.
          </>,
          <>Vor großem Versand: kleine Test-Liste (5-10 Adressen von dir/Kollegen) probieren.</>,
        ]}
        glossary={[
          {
            term: "CSV",
            def: "Einfache Tabellen-Datei. Jede Zeile = ein Eintrag, Werte mit Komma getrennt. Excel kann das speichern.",
          },
          {
            term: "Bounce",
            def: "Adresse existiert nicht oder Postfach ist voll — die Mail kommt zurück.",
          },
          {
            term: "Message-ID",
            def: "Eindeutige Nummer jeder gesendeten Mail. Brauchen wir, um Bounces später richtig zuzuordnen.",
          },
        ]}
      />

      <Card>
        <CardHeader>
          <CardTitle>1. Kampagne waehlen</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Select
            value={campaignId}
            onValueChange={(v) => {
              setCampaignId(v);
              void loadShortToken(v);
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="Kampagne auswaehlen…" />
            </SelectTrigger>
            <SelectContent>
              {campaigns.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {shortToken && (
            <p className="text-xs text-muted-foreground">
              Kampagnen-Token: <code className="font-mono">{shortToken}</code>
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>2. CSV hochladen</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="text-xs text-muted-foreground">
            Akzeptierte Spalten: <code>email</code> oder <code>recipient_email</code> (Pflicht),{" "}
            <code>sender_email</code> (optional). Separator: Komma, Semikolon oder Tab.
          </div>
          <Input type="file" accept=".csv,text/csv,text/plain" onChange={handleFile} />
          <Label className="text-xs text-muted-foreground">…oder direkt einfuegen:</Label>
          <Textarea
            value={csvText}
            onChange={(e) => setCsvText(e.target.value)}
            placeholder={"email,sender_email\nlead@example.com,alice@meineasykredit.click"}
            className="font-mono text-xs h-40"
          />
          <div className="flex items-center gap-3 text-sm">
            <Badge variant="secondary">{parsed.rows.length} gueltig</Badge>
            {parsed.errors.length > 0 && (
              <Badge variant="destructive">{parsed.errors.length} Fehler</Badge>
            )}
          </div>
          {parsed.errors.length > 0 && (
            <details className="text-xs text-muted-foreground">
              <summary>Fehlerdetails anzeigen</summary>
              <ul className="mt-2 list-disc pl-5">
                {parsed.errors.slice(0, 20).map((e, i) => (
                  <li key={i}>{e}</li>
                ))}
              </ul>
            </details>
          )}
          <Button onClick={handleImport} disabled={busy || !campaignId || parsed.rows.length === 0}>
            <Upload className="size-4 mr-2" />
            {busy ? "Importiere…" : `Importieren (${parsed.rows.length})`}
          </Button>
          {result && (
            <div className="rounded-md border p-3 text-sm space-y-1">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="size-4 text-emerald-500" />
                <span className="font-medium">Import abgeschlossen</span>
              </div>
              <div>
                Total: {result.total} &middot; Neu: {result.created} &middot; Aktualisiert:{" "}
                {result.updated} &middot; Fehler: {result.errors}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>3. Minimal-Tracking-Snippet</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Eine einzige <code>&lt;img&gt;</code>-Zeile. HTTPS, kein JavaScript, kein Branding,
            minimale Attribute. Plusvibe-Merge-Tags <code>{"{{sender_email}}"}</code> und{" "}
            <code>{"{{email}}"}</code> werden beim Versand ersetzt.
          </p>
          <div>
            <Label>Asset-Domain (neutral, frei waehlbar)</Label>
            <Input
              value={assetHost}
              onChange={(e) => setAssetHost(e.target.value)}
              placeholder="img.deinedomain.tld"
              list="recipient-import-asset-hosts"
            />
            <datalist id="recipient-import-asset-hosts">
              {ASSET_HOST_SUGGESTIONS.map((h) => (
                <option key={h} value={h} />
              ))}
            </datalist>
            <p className="text-xs text-muted-foreground mt-1">
              Frei waehlbare Subdomain wie <code>img</code>, <code>assets</code> oder{" "}
              <code>media</code>. Muss per CNAME auf <code>meineasykredit.click</code> zeigen.
              Default: Hauptdomain.
            </p>
          </div>
          {!shortToken ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Info className="size-4" /> Bitte zuerst Kampagne auswaehlen.
            </div>
          ) : (
            <>
              <pre className="rounded-md bg-muted p-3 text-xs overflow-x-auto whitespace-pre-wrap break-all">
                {minimalSnippet}
              </pre>
              <Button variant="outline" size="sm" onClick={() => copy(minimalSnippet)}>
                <Copy className="size-4 mr-2" /> Snippet kopieren
              </Button>
              <p className="text-xs text-muted-foreground">
                Fuege das Snippet direkt vor dem schliessenden <code>&lt;/body&gt;</code>-Tag deiner
                HTML-Mail ein.
              </p>
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>So funktioniert es</CardTitle>
        </CardHeader>
        <CardContent className="text-sm space-y-2 text-muted-foreground">
          <p>
            <strong className="text-foreground">Plusvibe</strong> versendet ganz normal mit
            rotierenden Absendern aus derselben CSV.
          </p>
          <p>
            <strong className="text-foreground">Lovable</strong> speichert hier Empfaenger +
            Kampagne + Sender, registriert Opens ueber das Pixel und matcht spaeter eingehende
            Bounces (IMAP-Scanner alle 2 Minuten) per Message-ID oder optional per VERP.
          </p>
          <p>
            VERP bleibt aktiv und wird automatisch als Fallback genutzt, falls die Message-ID nicht
            eindeutig zugeordnet werden kann.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
